import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { fetchServiceProvider } from "@/services/providers/providerCatalogService";
import { getProviderAnalytics } from "@/services/providers/providerAnalyticsService";
import { useAuth } from "@/contexts/auth";
import { useSubscription } from "@/hooks/useSubscription";
import LoadingSpinner from "@/components/LoadingSpinner";
import Layout from "@/components/Layout";
import PageHero from "@/components/layout/PageHero";
import {
  ArrowLeft,
  Calendar,
  Eye,
  Lock,
  MessageCircle,
  MousePointer,
  Phone,
  TrendingUp,
  Users
} from "lucide-react";

interface AnalyticsData {
  total_impressions: number;
  total_views: number;
  total_contacts: number;
  contact_breakdown: Record<string, number>;
  click_through_rate: number;
}

interface AnalyticsResponse {
  total_impressions: number;
  total_views: number;
  total_contacts: number;
  contact_breakdown: Record<string, number>;
  click_through_rate: number;
}

const ProviderAnalyticsPage = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const { user } = useAuth();
  const { data: subscriptionStatus } = useSubscription();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [providerName, setProviderName] = useState<string>("");
  const [hasAccess, setHasAccess] = useState(false);

  // Check if user has access to analytics
  useEffect(() => {
    const checkAccess = async () => {
      if (!user || !providerId) {
        setHasAccess(false);
        return;
      }

      // Check if user owns this provider
      const provider = await fetchServiceProvider(providerId);

      if (!provider || provider.user_id !== user.id) {
        setHasAccess(false);
        return;
      }

      // Check subscription status - allow access for whatsapp_tier and premium_tier
      const tier = subscriptionStatus?.tier || 'free_tier';
      const hasValidTier = tier === 'whatsapp_tier' || tier === 'premium_tier';
      
      setHasAccess(hasValidTier);
      setProviderName(provider.name || 'Unknown Provider');
    };

    checkAccess();
  }, [user, providerId, subscriptionStatus]);

  const fetchAnalytics = useCallback(async () => {
    if (!providerId || !user || !hasAccess) return;

    setLoading(true);
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      startDate.setDate(endDate.getDate() - days);

      // Fetch analytics using the database function
      const data = await getProviderAnalytics(
        providerId,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );

      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Type guard to ensure we have the right structure
        const analyticsResponse = data as unknown as AnalyticsResponse;
        if (
          typeof analyticsResponse.total_impressions === 'number' &&
          typeof analyticsResponse.total_views === 'number' &&
          typeof analyticsResponse.total_contacts === 'number'
        ) {
          setAnalyticsData({
            total_impressions: analyticsResponse.total_impressions || 0,
            total_views: analyticsResponse.total_views || 0,
            total_contacts: analyticsResponse.total_contacts || 0,
            contact_breakdown: analyticsResponse.contact_breakdown || {},
            click_through_rate: analyticsResponse.click_through_rate || 0
          });
        }
      }
    } catch (error) {
      console.error('Error in fetchAnalytics:', error);
    } finally {
      setLoading(false);
    }
  }, [providerId, user, hasAccess, dateRange]);

  useEffect(() => {
    if (hasAccess) {
      fetchAnalytics();
    } else {
      setLoading(false);
    }
  }, [hasAccess, fetchAnalytics]);

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-brand-mist">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  // Show access denied message for Starter users.
  if (!hasAccess) {
    const currentTier = subscriptionStatus?.tier || 'free_tier';
    const isFreeTier = currentTier === 'free_tier';
    
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-12">
          <div className="max-w-md rounded-md border border-brand-cloud bg-white p-8 text-center shadow-sm">
            <div className="mb-6">
              <Lock className="mx-auto mb-4 h-16 w-16 text-brand-slate" />
              <h1 className="mb-4 public-section-title">
                Analytics Access Restricted
              </h1>
              {isFreeTier ? (
                <p className="mb-6 text-brand-slate">
                  Listing analytics are included with WhatsApp Verified and Premium subscriptions. 
                  Upgrade when you want deeper insight into views, contacts, and listing performance.
                </p>
              ) : (
                <p className="mb-6 text-brand-slate">
                  You don't have permission to view analytics for this provider.
                </p>
              )}
            </div>
            
            <div className="space-y-3">
              {isFreeTier && (
                <div className="space-y-2">
                  <Link to="/pricing?checkout=whatsapp_tier" className="w-full">
                    <Button className="w-full bg-brand-ink text-white hover:bg-brand-charcoal">
                      Subscribe to WhatsApp Verified - HK$100/year
                    </Button>
                  </Link>
                  <Link to="/pricing?checkout=premium_tier" className="w-full">
                    <Button variant="outline" className="w-full border-brand-cloud text-brand-ink hover:bg-brand-cloud">
                      Subscribe to Premium - HK$500/year
                    </Button>
                  </Link>
                </div>
              )}
              
              <Link to="/user/listings" className="w-full">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to My Listings
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!analyticsData) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-12">
          <div className="max-w-md rounded-md border border-brand-cloud bg-white p-8 text-center shadow-sm">
            <h1 className="mb-4 public-section-title">Analytics Not Available</h1>
            <p className="mb-4 text-brand-slate">Unable to load analytics data for this provider.</p>
            <Link to="/user/listings">
              <Button className="w-full sm:w-auto">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to My Listings
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const contactBreakdown = analyticsData.contact_breakdown || {};

  return (
    <Layout>
      <div className="min-h-screen bg-brand-mist">
        <PageHero
          title="Provider Analytics"
          description={providerName}
          contentClassName="py-12 md:py-16"
        >
          <div className="flex max-w-3xl flex-col gap-2 sm:flex-row">
            <Link to={`/service/${providerId}`} className="w-full sm:w-auto">
              <Button variant="outline" className="w-full border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white hover:text-brand-ink sm:w-auto">
                <Eye className="h-4 w-4 mr-2" />
                View Listing
              </Button>
            </Link>
            <Link to="/user/listings" className="w-full sm:w-auto">
              <Button className="w-full bg-white text-brand-ink hover:bg-brand-cloud sm:w-auto">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Listings
              </Button>
            </Link>
          </div>
        </PageHero>

        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 sm:py-10 lg:px-8">

          {/* Mobile-optimized date range selector */}
          <div className="mb-6 flex flex-wrap gap-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <Button
                key={range}
                variant={dateRange === range ? "default" : "outline"}
                onClick={() => setDateRange(range)}
                size="sm"
                className="min-w-0 flex-1 sm:flex-none"
              >
                <span className="truncate">
                  {range === '7d' ? '7 days' : range === '30d' ? '30 days' : '90 days'}
                </span>
              </Button>
            ))}
          </div>

          <section className="mb-6 overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm sm:mb-8">
            <div className="border-b border-brand-cloud p-6 md:p-8">
              <h2 className="public-section-title">Performance overview</h2>
              <p className="mt-2 text-sm leading-6 text-brand-slate">
                Activity for the selected date range.
              </p>
            </div>

            <div className="grid divide-y divide-brand-cloud sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
              {[
                {
                  label: "Total Impressions",
                  value: analyticsData.total_impressions.toLocaleString(),
                  description: "Times your listing was seen",
                  icon: Eye,
                },
                {
                  label: "Profile Views",
                  value: analyticsData.total_views.toLocaleString(),
                  description: "Visits to your profile page",
                  icon: MousePointer,
                },
                {
                  label: "Contact Attempts",
                  value: analyticsData.total_contacts.toLocaleString(),
                  description: "WhatsApp, phone, website clicks",
                  icon: Users,
                },
                {
                  label: "Click-Through Rate",
                  value: `${analyticsData.click_through_rate}%`,
                  description: "Impressions to profile views",
                  icon: TrendingUp,
                },
              ].map((metric) => {
                const Icon = metric.icon;

                return (
                  <div key={metric.label} className="p-6">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-brand-slate">{metric.label}</p>
                      <Icon className="h-4 w-4 text-brand-slate" />
                    </div>
                    <p className="font-heading text-h2 font-semibold text-brand-ink">{metric.value}</p>
                    <p className="mt-2 text-sm text-brand-slate">{metric.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mb-6 overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm sm:mb-8">
            <div className="grid gap-6 p-6 md:grid-cols-[220px_1fr] md:p-8">
              <div>
                <h2 className="public-section-title">Contact methods</h2>
                <p className="mt-2 text-sm leading-6 text-brand-slate">How users are contacting you.</p>
              </div>

              <dl className="grid gap-0 overflow-hidden rounded-md border border-brand-cloud sm:grid-cols-3">
                {[
                  { label: "WhatsApp", value: contactBreakdown.whatsapp || 0, icon: MessageCircle, className: "text-whatsapp" },
                  { label: "Phone", value: contactBreakdown.phone || 0, icon: Phone, className: "text-harbour" },
                  { label: "Website", value: contactBreakdown.website || 0, icon: Calendar, className: "text-plum" },
                ].map((method) => {
                  const Icon = method.icon;

                  return (
                    <div key={method.label} className="border-b border-brand-cloud p-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
                      <dt className="flex items-center gap-2 text-sm font-medium text-brand-slate">
                        <Icon className={`h-4 w-4 ${method.className}`} />
                        {method.label}
                      </dt>
                      <dd className="mt-2 font-heading text-h3 font-semibold text-brand-ink">{method.value}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          </section>

          <section className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm">
            <div className="border-b border-brand-cloud p-6 md:p-8">
              <h2 className="public-section-title">Performance tips</h2>
              <p className="mt-2 text-sm leading-6 text-brand-slate">Ways to improve your listing performance.</p>
            </div>
            <div className="p-6 md:p-8">
              <div className="space-y-3">
                {analyticsData.click_through_rate < 5 && (
                  <div className="rounded-md border border-amber bg-amber-soft p-4">
                    <h4 className="font-semibold text-brand-ink">Low Click-Through Rate</h4>
                    <p className="text-sm text-brand-slate">
                      Consider updating your listing description, adding more photos, or highlighting your key services to attract more clicks.
                    </p>
                  </div>
                )}
                
                {analyticsData.total_contacts === 0 && (
                  <div className="rounded-md border border-harbour bg-harbour-soft p-4">
                    <h4 className="font-semibold text-brand-ink">No Contact Attempts</h4>
                    <p className="text-sm text-brand-slate">
                      Make sure your WhatsApp number is verified and clearly matches the number on your account so users can reach you.
                    </p>
                  </div>
                )}
                
                <div className="rounded-md border border-teal bg-teal-soft p-4">
                  <h4 className="font-semibold text-teal">Keep Your Listing Updated</h4>
                  <p className="text-sm text-brand-slate">
                    Regular updates to your description, services, and contact information help maintain visibility in search results.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default ProviderAnalyticsPage;
